/* eslint-disable no-magic-numbers, max-lines */
import moment from "moment";
import store from "@/store";
import { v4 as uuid } from "uuid";
import EnvironmentConfig from "./EnvironmentConfig";
import { isClosedThatDay, isInExceptionalDays } from "@/helpers/closeDates";

export default {
	warn(...messages) {
		if (process.env.NODE_ENV === "development") {
			// eslint-disable-next-line no-console
			console.warn(...messages);
		}
	},

	applyLessThanOneNumberFormat(value) {
		return value < 1 ? "<1" : Math.round(value);
	},

	titleCase(str) {
		return str.split(" ").map( val => val.charAt(0).toUpperCase() + val.substr(1).toLowerCase())
			.join(" ");
	},

	round(value) {
		return parseFloat(value).toFixed(1);
	},

	UTCToLocalTime(time) {
		const utcOffset = moment.parseZone(new Date()).utcOffset();
		return moment.utc(time).utcOffset(utcOffset);
	},

	/**
	 * Sort an array of object by the sortKey which is a key of the object in desc order.
	 * given:
	 * sortDesc([{cost: 2}, {cost: 1}, {cost: 3}], "cost")
	 *
	 * it will return:
	 * [{cost: 3}, {cost: 2}, {cost: 1}]
	 */
	sortDesc(array, sortKey = null) {
		// make immutable copy
		array = [...array];
		if (sortKey)
			return array.sort((obj1, obj2) => obj2[sortKey] - obj1[sortKey]);

		return array.sort((val1, val2) => val2 - val1);
	},

	/**
	 * Sort an array of object by the sortKey which is a key of the object in asc order.
	 * given:
	 * sortAsc([{cost: 2}, {cost: 1}, {cost: 3}], "cost")
	 *
	 * it will return:
	 * [ {cost: 1}, {cost: 2},{cost: 3}]
	 */
	sortAsc(array, sortKey = null) {
		// make immutable copy
		array = [...array];
		if (sortKey)
			return array.sort((obj1, obj2) => obj1[sortKey] - obj2[sortKey]);

		return array.sort((val1, val2) => val1 - val2);
	},


	/**
	 * Transform an object that has dates as keys, to a sorted array,
	 * because JS cannot guarantee the sort in Object.keys.
	 *
	 * given we have a `obj` argument:
	 * {
	 *		"2020-10": {"value": 1},
	 *		"2020-12": {"value": 3 },
	 *		"2020-11": {"value": null },
	 * }
	 * the return value will be:
	 * [
	 *		{"__date": "2020-10", "value": 1 },
	 *		{"__date": "2020-12", "value": null },
	 *		{"__date": "2020-12", "value": 3 }
	 * ]
	 *
	 */
	dateKeyObjectToSortedArray(obj, sort = "asc") {
		const dates = Object.entries(obj).map(([ date, value ]) => {
			return {
				__date: moment(date),
				...value
			};
		});

		if (sort === "asc")
			return this.sortAsc(dates, "__date");

		return this.sortDesc(dates, "__date");
	},

	// eslint-disable-next-line complexity
	returnFiltersState({
		activeDateOverride, activePeriodOverride, activeCompareToOverride
	}) {
		const activeDate = activeDateOverride || store.getters["filters/activeDate"],
			activePeriod = activePeriodOverride || store.getters["filters/activePeriod"],
			activeCompareTo = activeCompareToOverride || store.getters["filters/activeCompareTo"],
			deployments = store.getters["user/deployments"],
			selectedDevicesFilter = store.getters["user/selectedDevicesFilter"],
			activeOutlet = store.getters["user/activeOutlet"];

		let startDateCurrent,
			endDateCurrent,
			startDateCompare,
			endDateCompare,
			average;

		switch (activePeriod) {
		case "day":
			startDateCurrent = moment(activeDate);
			endDateCurrent = moment(activeDate).add(1, "day");

			switch (activeCompareTo) {
			case "day_before":
				startDateCompare = moment(activeDate).subtract(1, "day");
				endDateCompare = moment(activeDate).subtract(0, "day");
				break;

			case "same_day_week_before":
				startDateCompare = moment(activeDate).subtract(7, "day");
				endDateCompare = moment(activeDate).subtract(6, "day");
				break;

			case "average_day":
				average = "day";
				startDateCompare = moment(activeOutlet.outlet_start_date);
				endDateCompare = moment(activeOutlet.outlet_data_validity).add(1, "day");
				break;

			case "base_day":
				average = "day";
				startDateCompare = moment(activeOutlet.baseline.start_date);
				endDateCompare = moment(activeOutlet.baseline.end_date).add(1, "day");
				break;
			}

			break;

		case "week":
			startDateCurrent = moment(activeDate).startOf("isoWeek");
			endDateCurrent = moment(activeDate).endOf("isoWeek")
				.add(1, "days");

			switch (activeCompareTo) {
			case "previous_week":
				startDateCompare = moment(activeDate).subtract(1, "isoWeek")
					.startOf("isoWeek");
				endDateCompare = moment(activeDate).subtract(1, "isoWeek")
					.endOf("isoWeek")
					.add(1, "days");
				break;

			case "average_week":
				average = "week";
				startDateCompare = moment(activeOutlet.outlet_start_date).startOf("isoWeek");
				endDateCompare = moment(activeOutlet.outlet_data_validity).endOf("isoWeek")
					.add(1, "days");
				break;

			case "base_week":
				average = "week";
				startDateCompare = moment(activeOutlet.baseline.start_date).startOf("isoWeek");
				endDateCompare = moment(activeOutlet.baseline.end_date).endOf("isoWeek")
					.add(1, "days");
				break;
			}

			break;

		case "month":
			startDateCurrent = moment(activeDate).startOf("month");
			endDateCurrent = moment(activeDate).endOf("month")
				.add(1, "days");

			switch (activeCompareTo) {
			case "previous_month":
				startDateCompare = moment(activeDate).subtract(1, "month")
					.startOf("month");
				endDateCompare = moment(activeDate).subtract(1, "month")
					.endOf("month")
					.add(1, "days");

				break;

			case "average_month":
				average = "month";
				startDateCompare = moment(activeOutlet.outlet_start_date).startOf("month");
				endDateCompare = moment(activeOutlet.outlet_data_validity).endOf("month");
				break;

			case "base_month":
				average = "month";
				startDateCompare = moment(activeOutlet.baseline.start_date).startOf("month");
				endDateCompare = moment(activeOutlet.baseline.end_date).endOf("month");
				break;
			}

			break;

		case "year":
			// do NOT touch this; moment has some peculiarities that have to be respected...
			startDateCurrent = moment(activeDate).startOf("year");
			endDateCurrent = moment(activeDate).add(1, "year");
			endDateCurrent = endDateCurrent.startOf("year");
			// If no previous year to compare to, set to empty string
			if (!activeCompareTo || isNaN(activeCompareTo)) {
				startDateCompare = "";
				endDateCompare = "";
			}
			else {
				startDateCompare = moment(activeCompareTo + "-01-01").startOf("year");
				endDateCompare = moment(activeCompareTo + "-01-02").add(1, "years");
				endDateCompare = endDateCompare.startOf("year");
			}
			break;
		}

		return {
			dates: {
				start_date_current: startDateCurrent.format("DD/MM/YYYY"),
				end_date_current: endDateCurrent.format("DD/MM/YYYY"),
				start_date_compare: typeof startDateCompare === "string" ? startDateCompare : startDateCompare.format("DD/MM/YYYY"),
				end_date_compare: typeof endDateCompare === "string" ? endDateCompare : endDateCompare.format("DD/MM/YYYY")
			},
			activeOutletId: store.getters["user/activeOutlet"].outlet_id,
			activePropertyId: store.getters["user/activeProperty"].property_id,

			// if id is `null` don't send anything for device
			activeDevicesIds: selectedDevicesFilter.length ===
				deployments.reduce((len, deployment) => {
					return len + deployment.deployment_device_ids.length;
				}, 0)
				? undefined
				: selectedDevicesFilter.toString(),
			activeWasteType: store.getters["filters/activeWasteType"],
			average
		};
	},

	returnUserId() {
		let userId = "";
		const userData = JSON.parse(localStorage.getItem(localStorage.getItem("refkey") + ".userData")).UserAttributes;

		if (userData.length) {
			for (let index = 0; index < userData.length; index++) {
				if (userData[index].Name === "sub")
					userId = userData[index].Value;

			}
		}

		return userId;
	},

	returnUserEmail() {
		const userData = JSON.parse(localStorage.getItem(localStorage.getItem("refkey") + ".userData")).UserAttributes;
		let email = "";

		if (userData.length) {
			for (let index = 0; index < userData.length; index++) {
				if (userData[index].Name === "email")
					email = userData[index].Value;

			}
		}

		return email;
	},

	_getLanguage() {
		return localStorage.getItem("language") || "en";
	},

	initMixpanelBeforeTrack(instance) {
		const language = this._getLanguage(),
			activeProperty = store.getters["user/activeProperty"],
			activeOutlet = store.getters["user/activeOutlet"],
			user = store.getters["user/user"],
			userData = JSON.parse(localStorage.getItem(localStorage.getItem("refkey") + ".userData")).UserAttributes,
			email = userData.find(data => data.Name === "email").Value,
			userId = userData.find(data => data.Name === "sub").Value;

		instance.$ma.setSuperProperties({
			Outlet: activeProperty.property_name,
			Email: email,
			Language: language
		});

		instance.$ma.identify({
			userId: userId,
			options: {newUser: true}
		});

		instance.$ma.setUserProperties({
			userId: userId,
			$email: email,
			$first_name: activeProperty.property_name,
			$last_name: activeOutlet.outlet_name,
			Role: user.role
		});
	},

	capitalizeFirstLetter(string) {
		const FIRST_LETTER = 0,
			SLICE_START = 1;

		return string.charAt(FIRST_LETTER).toUpperCase() + string.slice(SLICE_START);
	},

	addUniqueKey(obj) {
		obj.__key = uuid();
		return obj;
	},

	removeUniqueKey(obj) {
		// eslint-disable-next-line no-unused-vars
		const {__key, ...objWithouthKey} = obj;
		return objWithouthKey;
	},

	/**
	 * Check if the active outlet was closed that date.
	 */
	isClosed(date) {
		const isClosed = isClosedThatDay(date);

		if (isClosed)
			return isClosed;

		// eslint-disable-next-line one-var
		const isExceptionalDay = isInExceptionalDays(date);

		return isExceptionalDay;
	},

	/**
	 * Shallow copy
	 */
	clone(value) {
		return JSON.parse(JSON.stringify(value));
	},

	getIconFromIconPath(path) {
		return path.replace("s3://", "https://s3.eu-central-1.amazonaws.com/");
	},

	getImageFromImageId(key, edits) {
		const path = {
			bucket: EnvironmentConfig.imagesBucketName,
			key,
			edits,
		};

		return `${EnvironmentConfig.imagesHostUrl}/${btoa(JSON.stringify(path))}`;
	},

	convertArrayToTranslatedObject(array, prefix) {
		return array.reduce((total, current) => ({
			...total,
			[current]: this.$t(`${prefix}${current}`),
		}), {});
	},

	/**
	 * range(4, 8) -> [4, 5, 6, 7, 8]
	 */
	range(start, end) {
		const rangeList = [];
		for (let index = start; index <= end; index += 1)
			rangeList.push(index);

		return rangeList;
	},

	/**
	 * moment(date)("MMM. YYYY") will in EN output `Dec. 2020`, but for FR. `Dez.. 2020`
	 * this function will replace each "Dez.. 2019" to be `Dez. 2019`
	 */
	replaceDoubleDotWithSingleDot(text) {
		return text.replace(/\.\./g, ".");
	},

	getDateRange(start, end) {
		return [ ...Array(moment(end).diff(start, "days")).fill(0)
			.map((current, index) => {
				return moment(start).add(index, "days")
					.format("YYYY-MM-DD");
			}), end.format("YYYY-MM-DD") ];
	},

	getPeriodRange(start, end, format = "YYYY-MM-DD", period = "days") {
		return [ ...Array(moment(end).diff(start, period)).fill(0)
			.map((current, index) => {
				return moment(start).add(index, period)
					.format(format);
			}), moment(end).format(format) ];
	},

	printPage(instance) {
		window.print();

		instance.$ma.trackEvent({
			action: "Printing page",
			properties: {
				"Value": null,
				"Component": instance.$route.name
			}
		});
	}
};

/**
 * Given a number `123456789` it will return `123'456'789`
 */
export function suffixNumber(number) {
	return number.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1’");
}

// Divides nom by denom. If denom is 0, it returns 0
export function divideOrZero(nom, denom) {
	return denom ? nom / denom : 0;
}

/*
 * Creates the content for a tooltip component.
 * Parameters:
 *   - headline (string): title of the tooltip. Leave it undefined if no title is needed.
 *   - texts (array of strings): paragraphs within the tooltip.
 */
export function createTooltipContent({headline, texts}) {
	const content = headline
		? [{
			type: "headline",
			text: headline
		}]
		: [];

	return content.concat(texts.map(text => ({
		type: "text",
		text: text
	})));
}
